import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity, faArrowDown, faArrowUp, faCoins, faChartBar,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import UiPageHeader from '../../components/ui/UiPageHeader';
import UiStatsGrid from '../../components/ui/UiStatsGrid';
import UiFilterPanel from '../../components/ui/UiFilterPanel';

dayjs.extend(utc);
dayjs.extend(timezone);

interface BankRow {
  bank: { id: string; name: string; bank_code: string; account_number: string } | null;
  total_deposit: number;
  total_withdrawal: number;
  deposit_commission: number;
  withdrawal_commission: number;
  total_commission: number;
}

interface Summary {
  total_deposit: number;
  total_withdrawal: number;
  total_deposit_commission: number;
  total_withdrawal_commission: number;
  total_commission: number;
}

const fmt = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BKK = 'Asia/Bangkok';
const getFirstDayOfMonth = () => dayjs().tz(BKK).startOf('month').format('YYYY-MM-DD');
const getTodayStr        = () => dayjs().tz(BKK).format('YYYY-MM-DD');

const bankCodeColors: Record<string, string> = {
  SCB: 'bg-purple-100 text-purple-700',
  KBANK: 'bg-green-100 text-green-700',
  BBL: 'bg-blue-100 text-blue-700',
  KTB: 'bg-sky-100 text-sky-700',
  BAY: 'bg-yellow-100 text-yellow-700',
  GSB: 'bg-pink-100 text-pink-700',
};

const AgentDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [agentUser, setAgentUser] = useState<any>(null);
  const [rows, setRows] = useState<BankRow[]>([]);
  const [summary, setSummary] = useState<Summary>({
    total_deposit: 0, total_withdrawal: 0,
    total_deposit_commission: 0, total_withdrawal_commission: 0, total_commission: 0
  });
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayStr());

  useEffect(() => {
    const stored = localStorage.getItem('agent_user');
    if (stored) setAgentUser(JSON.parse(stored));
    fetchReport(getFirstDayOfMonth(), getTodayStr());
  }, []);

  const fetchReport = useCallback(async (from: string, to: string) => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/agent/report', {
        params: { date_from: from, date_to: to }
      });
      if (res.data.success) {
        setRows(res.data.data.rows);
        setSummary(res.data.data.summary);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/agent/login');
      } else {
        toast.error('ดึงข้อมูล report ไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleSearch = () => fetchReport(dateFrom, dateTo);

  const handleReset = () => {
    const f = getFirstDayOfMonth();
    const t = getTodayStr();
    setDateFrom(f);
    setDateTo(t);
    fetchReport(f, t);
  };

  const stats = [
    {
      icon: faArrowDown,
      iconClassName: 'bg-green-100 text-green-600',
      label: 'ยอดฝากรวม',
      value: <span className="text-green-600">฿{fmt(summary.total_deposit)}</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-amber-100 text-amber-600',
      label: 'ยอดถอนรวม',
      value: <span className="text-amber-600">฿{fmt(summary.total_withdrawal)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-blue-100 text-blue-600',
      label: 'ค่าคอมฝาก',
      value: <span className="text-blue-600">฿{fmt(summary.total_deposit_commission)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-purple-100 text-purple-600',
      label: 'ค่าคอมถอน',
      value: <span className="text-purple-600">฿{fmt(summary.total_withdrawal_commission)}</span>,
    },
    {
      icon: faChartBar,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รวมค่าคอมทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>฿{fmt(summary.total_commission)}</span>,
    },
  ];

  return (
    <div>
      <UiPageHeader
        icon={faChartBar}
        title="รายงานค่าคอมมิชชั่น"
        subtitle={
          agentUser
            ? `ฝาก ${agentUser.deposit_commission_rate ?? 0}% · ถอน ${agentUser.withdrawal_commission_rate ?? 0}% · คำนวณจากธุรกรรมธนาคารที่ผูกไว้`
            : 'คำนวณจากธุรกรรมธนาคารที่ผูกไว้'
        }
      />

      {/* Filter */}
      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      />

      {/* Stats */}
      <UiStatsGrid cards={stats} loading={loading} cols={5} />

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-theme overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <FontAwesomeIcon icon={faUniversity} className="text-primary" />
          <h2 className="font-semibold text-gray-800">รายการตามธนาคาร</h2>
          <span className="ml-auto text-xs text-gray-400">{rows.length} รายการ</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary text-xl" />
            <span className="text-gray-500 text-sm">กำลังโหลด...</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FontAwesomeIcon icon={faChartBar} className="text-gray-300 text-4xl mb-3" />
            <p className="text-gray-500 font-medium">ไม่มีข้อมูลในช่วงเวลาที่เลือก</p>
            <p className="text-gray-400 text-sm mt-1">ลองเปลี่ยนช่วงวันที่แล้วค้นหาใหม่</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ธนาคาร</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">ยอดฝากรวม</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">ค่าคอมฝาก</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">ยอดถอนรวม</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">ค่าคอมถอน</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">รวมค่าคอม</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                    <td className="px-5 py-4">
                      {row.bank ? (
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${bankCodeColors[row.bank.bank_code] || 'bg-gray-100 text-gray-600'}`}>
                            {row.bank.bank_code}
                          </span>
                          <div>
                            <p className="font-medium text-gray-800">{row.bank.name}</p>
                            <p className="text-xs text-gray-400">{row.bank.account_number}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">ไม่ทราบธนาคาร</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-green-600">฿{fmt(row.total_deposit)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-green-700">฿{fmt(row.deposit_commission)}</td>
                    <td className="px-4 py-4 text-right font-medium text-amber-600">฿{fmt(row.total_withdrawal)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-amber-700">฿{fmt(row.withdrawal_commission)}</td>
                    <td className="px-5 py-4 text-right font-bold text-gray-800">฿{fmt(row.total_commission)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="px-5 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">รวมทั้งหมด</td>
                  <td className="px-4 py-4 text-right font-bold text-green-600">฿{fmt(summary.total_deposit)}</td>
                  <td className="px-4 py-4 text-right font-bold text-green-700">฿{fmt(summary.total_deposit_commission)}</td>
                  <td className="px-4 py-4 text-right font-bold text-amber-600">฿{fmt(summary.total_withdrawal)}</td>
                  <td className="px-4 py-4 text-right font-bold text-amber-700">฿{fmt(summary.total_withdrawal_commission)}</td>
                  <td className="px-5 py-4 text-right text-base font-bold" style={{ color: 'var(--theme-primary)' }}>
                    ฿{fmt(summary.total_commission)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboardPage;
